import type { Ref } from 'vue';
import { StatusCodes } from 'http-status-codes';
import type { Entry } from 'contentful';
import type {
  EntryWithLinkData,
  IContentPage,
  LOCALE_CODE
} from '~/lib/ContentfulService';
import { resolveEntry } from '~~/utils/resolveEntry';
import { Tracer } from '~/lib/Tracing';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';

export type UseContentfulPageResponseModel = Ref<{
  page: IContentPage;
  /**
   * Contains a locale slug map which maps a locale to a slug in that locale.
   * This map may contain `null` as entry whereas there actually exists
   * a matching slug, as resolving the link map is allowed to fail
   * in the api route.
   */
  localeSlugMap: Partial<Record<LOCALE_CODE, string>>;
  /**
   * Contains English topic slugs mapped by topic ID for analytics tracking
   */
  topicSlugMapEn: Record<string, string>;
}>;

export function useContentfulPage({
  id,
  slug,
  locale
}: {
  id?: string;
  slug?: string;
  locale: string | undefined;
}): Promise<UseContentfulPageResponseModel> {
  const tracer = new Tracer('composables');
  const { $globalPageSettings } = useNuxtApp();
  const abortControllerRef = ref<AbortController | null>(null);

  // the download center can have sub routes, so we simply overwrite the
  // requested slug if we are on a slug starting with download center slug.
  // this solution might be optimized in the future
  // @see https://gcp.baslerweb.com/jira/browse/DBP-930
  const documentCenterSlug =
    $globalPageSettings.value?.documentCenterPage?.slug?.toLowerCase();
  if (
    documentCenterSlug &&
    slug?.toLowerCase().startsWith(documentCenterSlug)
  ) {
    slug = documentCenterSlug;
  }
  const softwareCenterSlug =
    $globalPageSettings.value?.softwareCenterPage?.slug?.toLowerCase();
  if (
    softwareCenterSlug &&
    slug?.toLowerCase().startsWith(softwareCenterSlug)
  ) {
    slug = softwareCenterSlug;
  }

  return tracer.withSpan(
    'useContentfulPage',
    {
      'parameter.id': id,
      'parameter.slug': slug,
      'parameter.locale': locale
    },
    async () => {
      if (!abortControllerRef.value?.signal.aborted) {
        abortControllerRef.value?.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.value = abortController;
      const { data, error } = await useFetch('/api/contentful', {
        ...DEFAULT_FETCH_OPTIONS,
        query: {
          id,
          slug,
          locale
        },
        signal: abortController.signal
      });

      if (error.value) {
        throw createError({
          statusCode: error.value.statusCode,
          cause: error.value,
          fatal: true
        });
      }

      if (!data.value) {
        throw createError({
          statusCode: StatusCodes.NOT_FOUND,
          message: 'Page not found',
          fatal: true
        });
      }

      return computed(() => ({
        page: resolveEntry(
          data.value!.entry as EntryWithLinkData<Entry<unknown>>,
          true
        ) as IContentPage,
        localeSlugMap: data.value!.localeSlugMap,
        topicSlugMapEn: data.value!.topicSlugMapEn || {}
      }));
    }
  );
}
